// src/features/services/sales/api/bomApi.ts
// ─────────────────────────────────────────────────────────────────────────────
// Bill of Materials — FE API layer.
// All calls go through the BFF; zero orchestrator internals leak here.
// ─────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/bom';

// ── Types ────────────────────────────────────────────────────────────────────

export interface BomComponent {
  sku: string;
  quantity: number;
  unit: string;
  label: string;
  notes?: string;
}

export interface BomDefinition {
  bom_key: string;
  id_scope: number | null;
  finished_sku: string;
  label: string;
  version: string;
  active: boolean;
  components: BomComponent[];
  notes: string;
  created_by: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface UpsertBomPayload {
  finished_sku: string;
  label?: string;
  version?: string;
  components: Array<{
    sku: string;
    quantity: number;
    unit?: string;
    label?: string;
    notes?: string;
  }>;
  notes?: string;
}

export interface BomListResponse {
  items: BomDefinition[];
  next_cursor: string | null;
}

// ── Query Keys ───────────────────────────────────────────────────────────────

export const QK_BOMS = ['production', 'bom'] as const;

// ── API functions ────────────────────────────────────────────────────────────

export async function upsertBomApi(payload: UpsertBomPayload): Promise<BomDefinition> {
  return bff.post<BomDefinition, UpsertBomPayload>(BASE, payload);
}

export async function fetchBomsApi(
  params?: { active?: boolean; limit?: number },
): Promise<BomListResponse> {
  return bff.get<BomListResponse>(BASE, params as any);
}

export async function fetchBomApi(bomKey: string): Promise<BomDefinition> {
  return bff.get<BomDefinition>(`${BASE}/${encodeURIComponent(bomKey)}`);
}

export async function deactivateBomApi(bomKey: string): Promise<BomDefinition> {
  return bff.post<BomDefinition, undefined>(`${BASE}/${encodeURIComponent(bomKey)}/deactivate`, undefined);
}

export async function reactivateBomApi(bomKey: string): Promise<BomDefinition> {
  return bff.post<BomDefinition, undefined>(`${BASE}/${encodeURIComponent(bomKey)}/reactivate`, undefined);
}
